import { Formik, useFormikContext } from 'formik';
import { useEffect, useRef, useContext, useState, useMemo } from 'react';
import { Form, Button, message, Modal, Image } from 'antd';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Result, ReplyMsgs, Description, Process, RelatedTradeItem } from './FormItem';
import { useDetail, useUpdateTodo, useCloseTodo, useReopenTodo } from './useApi';
import { convertMessageToTextAndImage } from './helper';
import { ReadonlyContext, ReadonlyProvider } from './context';
import Profile from './Profile';
import StatusButton from 'components/StatusButton';
import { useCurrentConversation } from 'hooks/business/conversation';
import { MessagePreviewer } from 'views/Conversation/Chat/components/Feedback/FeedbackForm';
import { reportCase } from 'services/api/feedback';
import { feedbackFormValues2submitValues } from 'views/Conversation/Chat/components/Feedback/utils';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useResultTypeLabel, useTypeLabel } from 'views/Todo/hooks/useLabel';
import useModal from 'hooks/useModal';
import { eventBus } from 'services/eventBus';
import { useSendFileImage } from 'views/Conversation/Chat/components/MessageEditor/Toolbar/ImageUpload';
import { generateInvoiceImage } from 'services/api/conversation';
import { CopyOutlined } from '@ant-design/icons';
import { isMac } from 'utils/is';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import posthog from '@leyan/lytics';
import { useKeyboardHandler } from 'hooks/useKeyboardManager';
import { KEYBOARD_PRIORITY } from 'hooks/useKeyboardManager/KeyboardEventManager';
import { getCloseTodoValidation } from './todoBusinessUtils';
import { useExtraFields } from './useExtraFields';
import { useGetMessageById } from 'hooks/business/message';

function Detail({
  onUpdate,
  readonly = false,
}: {
  onUpdate?: (todo?: Todo.TodoInstance, readonly?: boolean) => void;
  readonly?: boolean;
}) {
  const { detail } = useDetail();
  const updateTodo = useUpdateTodo();
  const { t } = useTranslate();
  const prevDetailRef = useRef<Todo.TodoInstance>(null!);

  const refsCurrent = { onUpdate };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;
  useEffect(() => {
    refs.current.onUpdate?.(detail);
  }, [detail]);

  if (!detail) {
    return null;
  }

  return (
    <Container>
      <Formik
        initialValues={detail}
        enableReinitialize
        initialTouched={{ relatedReturnId: true }}
        validateOnChange={false}
        onSubmit={(value) => {
          const {
            id,
            description,
            relatedTradeId,
            relatedItemId,
            relatedReturnId,
            operateResult,
            customOperateResult,
            testimonies,
            replyMsgs,
            operator,
            subType,
            subType2,
            subType3,
            type,
            typeName,
            subStatus,
            todoStatus,
            resultTypeStatus,
            relatedSku,
          } = value;
          const {
            description: pDescription,
            testimonies: pTestimonies,
            replyMsgs: pReplyMsgs,
          } = prevDetailRef.current || detail;
          let deleteFlgr = false;
          const { images: dImages } = convertMessageToTextAndImage(description);
          const { images: pdImages } = convertMessageToTextAndImage(pDescription);
          if (dImages?.length < pdImages?.length) {
            deleteFlgr = true;
          }
          if (testimonies?.length < pTestimonies?.length) {
            deleteFlgr = true;
          }
          const { images: rImages } = convertMessageToTextAndImage(replyMsgs);
          const { images: prImages } = convertMessageToTextAndImage(pReplyMsgs);
          if (rImages?.length < prImages?.length) {
            deleteFlgr = true;
          }
          updateTodo({
            id,
            description,
            relatedTradeId,
            relatedItemId,
            relatedReturnId,
            operateResult,
            customOperateResult,
            testimonies,
            operator,
            replyMsgs,
            subType,
            type,
            typeName,
            subStatus,
            status: todoStatus,
            subType2,
            subType3,
            resultTypeStatus,
            relatedSku,
          }).then(() => {
            prevDetailRef.current = value;
            message.success(
              deleteFlgr
                ? t('common.status.deleted')
                : t('scene.chatWorkspace.taskTab.feedback.saveSuccess'),
            );
            eventBus.emit('TODO_UPDATE', id);
          });
        }}
      >
        {({ values }) => (
          <ReadonlyProvider value={readonly}>
            <DetailForm value={values} key={values.id} />
          </ReadonlyProvider>
        )}
      </Formik>
    </Container>
  );
}

export function DetailForm(props: { value: Todo.TodoInstance }) {
  const { value } = props;

  const {
    id,
    operateResult,
    type,
    operationLog,
    relatedTradeId,
    relatedMsgId,
    todoStatus,
    storeId,
    source,
  } = value;

  const { whiteListConfig: { ERP } = {} } = useOrgConfigStateValue();

  const { setFieldError } = useFormikContext();
  const getMessageById = useGetMessageById();

  const closeTodo = useCloseTodo();
  const reopenTodo = useReopenTodo();

  const readonly = useContext(ReadonlyContext);

  const onCloseTodo = () => {
    const validation = getCloseTodoValidation(value);
    if (!validation.valid && validation.errorField && validation.errorMessageKey) {
      const msg =
        validation.errorMessageParams?.x != null
          ? t(validation.errorMessageKey, { x: t(validation.errorMessageParams.x) })
          : t(validation.errorMessageKey);
      setFieldError(validation.errorField, msg);
      document.getElementById(validation.errorField)?.scrollIntoView({ block: 'center' });
      return Promise.resolve();
    }
    return closeTodo(false, id || undefined);
  };

  const onReopenTodo = () => {
    return reopenTodo();
  };

  useKeyboardHandler(
    [isMac ? 'ctrl+w' : 'alt+w'],
    () => {
      if (todoStatus === 3) {
        reopenTodo();
      } else {
        onCloseTodo();
      }
      posthog.capture('$hotKey', {
        hotKey: `${isMac ? 'Ctrl' : 'Alt'}+w`,
        todoId: id,
      });
      return false;
    },
    {
      eventType: 'keydown',
      priority: KEYBOARD_PRIORITY.TOOLBAR,
      stopPropagation: true,
      preventDefault: true,
    },
  );

  const { getTypeLabel, t } = useTypeLabel();

  const { getResultTypeLabel } = useResultTypeLabel();

  const userLanguage = useSystemLanguageValue();

  const msg = useMemo(() => getMessageById(relatedMsgId), [getMessageById, relatedMsgId]);

  const [feedbackVisible, setFeedbackVisible] = useState(false);

  // 手动创建的不需要显示
  const feedbackModal = source === 0 && msg && (
    <Modal
      width={602}
      title={t('scene.chatWorkspace.taskTab.detail.feedback.recognitionError')}
      visible={feedbackVisible}
      onCancel={() => setFeedbackVisible(false)}
      okText={t('scene.chatWorkspace.taskTab.detail.action.submitAndClose')}
      onOk={() => {
        setFeedbackVisible(false);

        onCloseTodo();

        reportCase(
          feedbackFormValues2submitValues({
            message: msg,
            userLanguage,
            caseType: 'UN_ANSWERED',
            caseCategory: '待办问题识别错误',
            reason: getResultTypeLabel(operateResult),
          }),
        )
          .then(() =>
            message.success(t('scene.chatWorkspace.feedbackDialog.feedback.submitSuccess')),
          )
          .catch(() =>
            message.error(t('scene.chatWorkspace.feedbackDialog.feedback.submitFailed')),
          );
      }}
    >
      <MessagePreviewer showName={msg.fromUserName} message={msg} />
      <div style={{ marginTop: '14px' }}>
        {t('scene.chatWorkspace.taskTab.detail.label.scenario')}：{getTypeLabel(type)}
      </div>
    </Modal>
  );

  const sendFileImage = useSendFileImage();

  const {
    visible: invoiceVisible,
    show: showInvoice,
    hide: hideInvoice,
    meta: invoiceUrl,
  } = useModal<string>();

  const invoiceModal = invoiceUrl && (
    <Modal
      width={602}
      title={t('scene.chatWorkspace.orderTab.invoice.title.sendInvoice')}
      visible={invoiceVisible}
      onCancel={() => hideInvoice()}
      okText={t('scene.chatWorkspace.taskTab.detail.action.sendAndClose')}
      onOk={() => {
        hideInvoice();

        onCloseTodo();

        sendFileImage(invoiceUrl, () => eventBus.emit('ON_SEND_MESSAGE')).catch(() => {});
      }}
    >
      <Image src={invoiceUrl} />
    </Modal>
  );

  const disabled = todoStatus === 3 || readonly;

  const { buyerId } = useCurrentConversation() || {};

  return (
    <>
      <Content id="todo-detail-content">
        <div className="pb-[16px]">
          <Form layout="vertical">
            <Profile disabled={disabled} />
            <FormContainer>
              {ERP && [1, 2].includes(type) && <RelatedTradeItem />}
              <Result />
              {!disabled && <ReplyMsgs />}
              <Description />
              <ExtraFields value={value} />
              <Process operationLog={operationLog} type={type} storeId={storeId} />
            </FormContainer>
          </Form>
        </div>
      </Content>
      {!disabled ? (
        <ButtonContainer>
          {msg && source === 0 ? (
            [
              <Button type="link" onClick={() => setFeedbackVisible(true)}>
                {t('scene.chatWorkspace.taskTab.detail.label.feedback')}
              </Button>,
              feedbackModal,
            ]
          ) : (
            <div />
          )}
          <div>
            {operateResult === 'INVOICE' &&
              buyerId && [
                <StatusButton
                  onClick={() =>
                    generateInvoiceImage({
                      externalTradeId: relatedTradeId,
                      internalBuyerId: buyerId,
                      internalStoreId: storeId,
                    })
                      .then(showInvoice)
                      .catch(() => {
                        message.error(t('common.feedback.fetchFailed'));
                      })
                  }
                >
                  {t('scene.chatWorkspace.orderTab.invoice.label')}
                </StatusButton>,
                invoiceModal,
              ]}
            <StatusButton type="primary" onClick={onCloseTodo}>
              {`${t('common.action.close')} (${isMac ? 'ctrl' : 'alt'}+w)`}
            </StatusButton>
          </div>
        </ButtonContainer>
      ) : (
        todoStatus === 3 && (
          <ButtonContainer>
            <div></div>
            <StatusButton type="primary" onClick={onReopenTodo}>
              {`${t('scene.chatWorkspace.taskTab.operationLog.reopen')} (${
                isMac ? 'ctrl' : 'alt'
              }+w)`}
            </StatusButton>
          </ButtonContainer>
        )
      )}
    </>
  );
}

/** 纯展示：根据 useExtraFields 的 bag 渲染当前详情页的「额外字段」UI，其他模块可复用 hook 自绘 UI */
function ExtraFields({ value }: { value: Todo.TodoInstance }) {
  const { t } = useTranslate();
  const bag = useExtraFields(value);
  const { unit, hasContent, compensation, type8, type9, type10, type11, onCopy } = bag;

  if (!hasContent) {
    return null;
  }

  const copyIcon = (text: string) => (
    <CopyOutlined className="cursor-pointer text-[#515359]" onClick={() => onCopy(text)} />
  );

  return (
    <div className="rounded-[4px] bg-[#f7f9fa] p-[10px]">
      {compensation.visible && (
        <div>
          {t('scene.chatWorkspace.taskTab.detail.label.compensationAmount')}: {unit}{' '}
          {compensation.amount}
        </div>
      )}
      {type8.visible && (
        <>
          {!!type8.relatedTradeId && (
            <div>
              {t('domain.order.field.orderId')}: {type8.relatedTradeId}
              {copyIcon(type8.relatedTradeId)}
            </div>
          )}
          {!!type8.relatedTraceNum && (
            <div>
              {t('domain.order.logistics.field.trackingNumber')}: {type8.relatedTraceNum}
              {copyIcon(type8.relatedTraceNum)}
            </div>
          )}
          <div>
            {t('scene.chatWorkspace.taskTab.detail.label.sellerShippingFee')}: {type8.freightAmount}{' '}
            {unit}
          </div>
        </>
      )}
      {type9.visible && (
        <>
          {!!type9.relatedTradeId && (
            <div>
              {t('domain.order.field.orderId')}: {type9.relatedTradeId}
              {copyIcon(type9.relatedTradeId)}
            </div>
          )}
          {!!type9.orderTime && (
            <div>
              {t('domain.order.field.orderTime')}: {type9.orderTimeFormatted}
            </div>
          )}
          {!!type9.stillTime && (
            <div className="text-[#F05B5B]">
              {t('scene.chatWorkspace.taskTab.detail.label.waitingShipDuration')}: {type9.stillTime}
            </div>
          )}
        </>
      )}
      {type10.visible && (
        <>
          {!!type10.relatedTradeId && (
            <div>
              {t('domain.order.field.orderId')}: {type10.relatedTradeId}
              {copyIcon(type10.relatedTradeId)}
            </div>
          )}
          {!!type10.relatedTraceNum && (
            <div>
              {t('domain.order.logistics.field.trackingNumber')}: {type10.relatedTraceNum}
              {copyIcon(type10.relatedTraceNum)}
            </div>
          )}
          {!!type10.orderTime && (
            <div>
              {t('domain.order.field.orderTime')}: {type10.orderTimeFormatted}
            </div>
          )}
          {!!type10.stillTime && (
            <div className="text-[#F05B5B]">
              {t('scene.chatWorkspace.taskTab.detail.label.waitingPickupDuration')}:{' '}
              {type10.stillTime}
            </div>
          )}
        </>
      )}
      {type11.visible && (
        <>
          {!!type11.relatedTradeId && (
            <div>
              {t('domain.order.field.orderId')}: {type11.relatedTradeId}
              {copyIcon(type11.relatedTradeId)}
            </div>
          )}
          {!!type11.relatedTraceNum && (
            <div>
              {t('domain.order.logistics.field.trackingNumber')}: {type11.relatedTraceNum}
              {copyIcon(type11.relatedTraceNum)}
            </div>
          )}
          {!!type11.deliveryTime && (
            <div>
              {t('scene.chatWorkspace.taskTab.detail.label.shipTime')}:{' '}
              {type11.deliveryTimeFormatted}
            </div>
          )}
          {!!type11.stillTime && (
            <div className="text-[#F05B5B]">
              {t('scene.chatWorkspace.taskTab.detail.label.transitDuration')}: {type11.stillTime}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export const Container = styled.div`
  flex: 1 0 0;
  overflow: hidden;

  display: flex;
  flex-direction: column;
`;

const FormContainer = styled.div`
  margin-top: 16px;
  .ant-row.ant-form-item {
    margin-bottom: 16px;
    .ant-col {
      & > label {
        color: #515359 !important;
      }
    }
  }
  > form > div {
    margin-top: 16px;
  }
`;

const ButtonContainer = styled.div`
  padding: 12px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0px -2px 8px 0px rgba(167, 178, 204, 0.1),
    inset 0px 1px 0px 0px rgba(240, 242, 245, 1);
  > div {
    display: flex;
    align-items: center;
    > * {
      margin-left: 12px;
    }
  }
`;

const Content = styled.div`
  flex: 1 0 0;
  overflow: auto;
  padding-top: 15px;
  padding-left: 12px;
  padding-right: 16px;
  background-color: #fff;
`;

export default Detail;
